import basicController from "servicestack/basic.controller.properties";
import ServiceBus from "servicestack/service-bus";
import modals from "webmodals/modals";
import FormioExport from "formio-export";
import recaptcha from "emun.auth/recaptcha/recaptcha.service";
import isPlainObject from "lodash/isPlainObject";

const EVENTS = [
  "formSubmit",
  "formSubmission",
  "formError",
  "formLoad",
  "submissionView",
  "submissionEdit",
  "submissionDelete",
  "submissionLoad",
  "submissionsLoad",
  "cancel",
  "delete",
  "change",
];

const disableAutoComplete = () =>
  document
    .querySelectorAll("div.formio-component input.form-control")
    .forEach((c) => (c.autocomplete = "new-password"));

// see https://github.com/formio/ngFormio/wiki/Responding-to-Form-Events
export default ($ctrl, $scope) => {
  basicController($ctrl);
  $ctrl.$q = $ctrl.$ioc("$q");
  $ctrl.nestedForms = [];
  $ctrl.$loadedForms = [];
  // console.debug("ngFormio", { $scope, $ctrl });
  EVENTS.forEach((eventType) => {
    const et = `$${eventType}`;
    if (et in $ctrl) {
      $scope.$on(et, (e, args) => {
        // console.debug(et, { e, args });
        $ctrl[et](e, args);
      });
    }
  });
  [100, 1000, 2500, 5000, 10000].forEach((i) =>
    setTimeout(disableAutoComplete, i)
  );

  $ctrl.buildForm = buildForm.bind($ctrl);
  $ctrl.getFormIO = getFormIO.bind($ctrl);
  $ctrl.getFormSubmission = getFormSubmission.bind($ctrl);
  $ctrl.getNestedForms = getNestedForms.bind($ctrl);
  $ctrl.exportFormIO = exportFormIO.bind($ctrl);
  $ctrl.getKeys = getKeys.bind($ctrl);
  $ctrl.getComponentByKey = getComponentByKey.bind($ctrl);
  $ctrl.getFormComponent = getFormComponent.bind($ctrl);

  const onFormChanged = (e, submission) => {
    //console.debug("formChanged", { e, submission });
    e.preventDefault();
    if ($ctrl.formChanged) {
      $ctrl.formChanged(e, submission);
    }
  };

  const onFormSubmitted = (e, submission) => {
    // console.debug("formSubmit", { e, submission });
    e.preventDefault();
    return recaptcha
      .verify(recaptcha.scrubAction(`FORMIO_${submission.label}`))
      .then(() => {
        $ctrl.formSubmitting && $ctrl.formSubmitting(e, submission);
        $ctrl.formProcessing = true;
        submission.active = !!submission.data.submit;

        return ($ctrl.bus || ServiceBus)
          .send(
            submission.id
              ? "UpdateFormSubmission.json"
              : "CreateFormSubmission.json",
            submission
          )
          .then((res) => {
            if (!submission.id) {
              $ctrl.nestedForms.forEach(function (nf) {
                var nestedSubmission = {
                  active: true,
                  state: "submitted",
                  label: nf.id,
                  metadata: submission.metadata,
                  data: {},
                  refId: res.data.id,
                };
                nf.keys.forEach(function (key) {
                  nestedSubmission.data[key] = submission.data[key];
                });
                if (
                  nestedSubmission.label === "CreditApplication" &&
                  Object.prototype.hasOwnProperty.call(
                    submission.data,
                    "doNotApplyForCredit"
                  )
                ) {
                  if (submission.data["doNotApplyForCredit"] === false) {
                    $ctrl.bus.send(
                      "CreateFormSubmission.json",
                      nestedSubmission
                    );
                  }
                } else {
                  $ctrl.bus.send("CreateFormSubmission.json", nestedSubmission);
                }
              });
            }
            if ($ctrl.formSubmission) {
              $ctrl.formProcessing = false;
              $ctrl.formSubmission(e, res.data, submission);
            }
          })
          .catch((res) => {
            $ctrl.formProcessing = false;
            modals.alert(res.data.responseStatus.message);
          })
          .finally(() => ($ctrl.formProcessing = false));
      })
      .finally(() => ($ctrl.formProcessing = false));
  };
  if ($scope) {
    $scope.$on("formSubmit", onFormSubmitted);
    $scope.$on("formSubmission", onFormSubmitted);
    $scope.$on("change", onFormChanged);
  }
};

function getComponentByKey(component, key) {
  const $ctrl = this;
  const results = [];
  if (Array.isArray(component)) {
    component.forEach(function (c) {
      if (c.components && c.components.length)
        results.push(...$ctrl.getComponentByKey(c.components, key));
      else if (c.columns && c.columns.length)
        results.push(...$ctrl.getComponentByKey(c.columns, key));
      else if (c.input && c.key === key) results.push(c);
    });
  } else {
    if (component.components && component.components.length)
      results.push(...$ctrl.getComponentByKey(component.components, key));
    else if (component.columns && component.columns.length)
      results.push(...$ctrl.getComponentByKey(component.columns, key));
    else if (component.input && component.key === key) results.push(component);
  }
  return results;
}

function getFormComponent(component, key) {
  const results = [];
  const $ctrl = this;
  if (Array.isArray(component)) {
    component.forEach(function (c) {
      if (c.formioId && c.formioId === key) results.push(c);
      else {
        if (c.components && c.components.length)
          results.push(...$ctrl.getFormComponent(c.components, key));
        else if (c.columns && c.columns.length)
          results.push(...$ctrl.getFormComponent(c.columns, key));
      }
    });
  } else {
    if (component.formioId && component.formioId === key)
      results.push(component);
    else {
      if (component.components && component.components.length)
        results.push(...$ctrl.getFormComponent(component.components, key));
      else if (component.columns && component.columns.length)
        results.push(...$ctrl.getFormComponent(component.columns, key));
    }
  }
  return results;
}

function getKeys(component) {
  const results = [];
  const $ctrl = this;
  if (Array.isArray(component)) {
    component.forEach(function (c) {
      if (c.components && c.components.length)
        results.push(...$ctrl.getKeys(c.components));
      else if (c.columns && c.columns.length)
        results.push(...$ctrl.getKeys(c.columns));
      else if (c.input) results.push(c.key);
    });
  } else {
    if (component.components && component.components.length)
      results.push(...$ctrl.getKeys(component.components));
    else if (component.columns && component.columns.length)
      results.push(...$ctrl.getKeys(component.columns));
    else if (component.input) results.push(component.key);
  }
  return results;
}

function getNestedForms(component) {
  const results = [];
  const $ctrl = this;

  if (Array.isArray(component)) {
    for (const i in component) {
      results.push(...$ctrl.getNestedForms(component[i]));
    }
  } else if (isPlainObject(component)) {
    for (const [key, value] of Object.entries(component)) {
      if (key === "formioId") {
        if (!$ctrl.$loadedForms.some((f) => f.id === value)) {
          results.push(
            $ctrl.getFormIO(value).then((nestedForm) => {
              if (nestedForm && nestedForm.label) {
                component.components = (component.components || []).concat(
                  nestedForm.fields.components.filter((c) => c.key !== "submit")
                );
                var formKeys = {
                  id: nestedForm.id,
                  keys: $ctrl.getKeys(nestedForm.fields.components),
                };
                $ctrl.nestedForms.push(formKeys);
              }
            })
          );
        }
      } else {
        results.push(...$ctrl.getNestedForms(value));
      }
    }
  }

  return results;
}

function buildForm({ data: { results: [form = {}] = [] } = {} }) {
  const $ctrl = this;
  $ctrl.$loadedForms.push(form);
  return $ctrl.$q.all($ctrl.getNestedForms(form)).then((resolutions) => {
    $ctrl.parentForm = form;
    // if (form.formType == "Registration") {
    //   form.label = "NewRetailerRequest";
    //   form.id = "NewRetailerRequest";
    // }
    return form;
  });
}

function getFormIO(formioId) {
  const $ctrl = this;
  return $ctrl.bus
    .send(`QueryFormConfigs/${formioId || "!RETURN_EMPTY!"}.json`)
    .then((r) => {
      return $ctrl.buildForm(r);
    });
}

function getFormSubmission(submissionId) {
  const $ctrl = this;
  return $ctrl.bus
    .send(`QueryFormSubmissions/${submissionId || "!RETURN_EMPTY!"}.json`)
    .then(({ data }) => {
      if (data.results.length) {
        return data.results[0];
      }
    });
}

function exportFormIO(formioId, submissionId) {
  const $ctrl = this;
  $ctrl.getFormSubmission(submissionId).then((submission) => {
    $ctrl.getFormIO(formioId).then((f) => {
      let component = {
        type: "form",
        title: submission.label,
        display: "form",
        components: f.fields.components,
      };
      let options = {
        ignoreLayout: true,
      };
      let config = {
        download: true,
        filename: submission.label + ".pdf",
      };
      let exporter = new FormioExport(component, submission.data, options);
      exporter.toPdf(config);
    });
  });
}
